import { useEffect, useRef } from 'react'
import { useThemeLabStore } from './store'

/**
 * Apply persisted overrides to :root immediately. Call once in main.tsx after
 * `initTheme()` so the overrides are already on `documentElement` by the time
 * React paints the first frame — avoids a brief flash of the default theme.
 */
export function initThemeLab(): void {
  const overrides = useThemeLabStore.getState().overrides
  for (const [name, value] of Object.entries(overrides)) {
    document.documentElement.style.setProperty(name, value)
  }
}

/**
 * Keep `documentElement` in sync with the Zustand overrides map. Mount once
 * near the app root so every token-sensitive surface picks up live changes.
 * Tracks the previous snapshot so tokens that get removed from the map also
 * get cleared from the DOM.
 */
export function useApplyThemeOverrides(): void {
  const overrides = useThemeLabStore((s) => s.overrides)
  const prevRef = useRef<Record<string, string>>({})
  useEffect(() => {
    const prev = prevRef.current
    for (const name of Object.keys(prev)) {
      if (!(name in overrides)) {
        document.documentElement.style.removeProperty(name)
      }
    }
    for (const [name, value] of Object.entries(overrides)) {
      document.documentElement.style.setProperty(name, value)
    }
    prevRef.current = { ...overrides }
  }, [overrides])
}

/**
 * Keyboard shortcut — `⌘⇧T` / `Ctrl+Shift+T` toggles the Theme Lab panel.
 * Only armed when `enabled` is true (admin check in AppShell). Uses `code`
 * so the binding is stable across keyboard layouts.
 */
export function useThemeLabShortcut(enabled: boolean): void {
  const toggle = useThemeLabStore((s) => s.toggle)
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.shiftKey && e.code === 'KeyT') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, toggle])
}
